import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Button, Space, Input, InputNumber, Typography, message, Tabs, Segmented,
  Modal, Form, Row, Col, Empty, Descriptions, Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SearchOutlined, CheckCircleFilled, EditOutlined, ExperimentOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import request from '@/utils/request';
import type {
  LLMModelSearchResult, LLMActiveModel, LLMSettings, LLMProvidersResponse, LLMTestResponse,
} from '@zero/shared';

const { Title, Text } = Typography;

const PROVIDERS: Array<{ key: string; label: string }> = [
  { key: 'openrouter', label: 'OpenRouter' },
  { key: 'closeai', label: 'CloseAI' },
];

function formatPrice(value: string | null): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!isFinite(n)) return '—';
  return `$${n.toFixed(n >= 1 ? 2 : 4)}`;
}

interface TestResultEntry {
  success: boolean;
  model?: string;
  latencyMs?: number;
  error?: string;
  testedAt: string;
}

const TEST_RESULTS_KEY = 'llm_test_results';

function loadTestResults(): Record<string, TestResultEntry> {
  try {
    const raw = localStorage.getItem(TEST_RESULTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveTestResults(map: Record<string, TestResultEntry>) {
  try {
    localStorage.setItem(TEST_RESULTS_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors
  }
}

const LLMModelsPage: React.FC = () => {
  const [activeProvider, setActiveProvider] = useState<string>(PROVIDERS[0].key);
  const [tabProvider, setTabProvider] = useState<string>(PROVIDERS[0].key);
  const [activeModels, setActiveModels] = useState<Record<string, LLMActiveModel | null>>({});
  const [providersInfo, setProvidersInfo] = useState<LLMProvidersResponse | null>(null);

  const [keyword, setKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<LLMModelSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const [settings, setSettings] = useState<LLMSettings | null>(null);
  const [markupInput, setMarkupInput] = useState<number>(1.5);

  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResultEntry>>(() =>
    loadTestResults(),
  );
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [priceModalTarget, setPriceModalTarget] = useState<LLMModelSearchResult | null>(null);
  const [priceForm] = Form.useForm<{
    displayName?: string;
    inputPricePerMtok: number;
    outputPricePerMtok: number;
  }>();

  const loadProviders = useCallback(async () => {
    try {
      const res = await request.get<LLMProvidersResponse>('/admin/llm/providers');
      setProvidersInfo(res.data);
      setActiveProvider(res.data.activeProviderId || PROVIDERS[0].key);
    } catch (err) {
      message.error((err as Error)?.message || '加载 Provider 状态失败');
    }
  }, []);

  const loadActive = useCallback(async (provider: string) => {
    try {
      const res = await request.get<LLMActiveModel | null>('/admin/llm/models/active', {
        params: { provider },
      });
      setActiveModels((prev) => ({ ...prev, [provider]: res.data }));
    } catch (err) {
      message.error((err as Error)?.message || '加载当前默认模型失败');
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await request.get<LLMSettings>('/admin/llm/settings');
      setSettings(res.data);
      setMarkupInput(res.data.closeaiMarkupRatio);
    } catch (err) {
      message.error((err as Error)?.message || '加载设置失败');
    }
  }, []);

  useEffect(() => {
    loadProviders();
    loadSettings();
  }, [loadProviders, loadSettings]);

  useEffect(() => {
    loadActive(tabProvider);
  }, [tabProvider, loadActive]);

  useEffect(() => {
    PROVIDERS.forEach((p) => loadActive(p.key));

  }, []);

  const handleSwitchActiveProvider = async (provider: string) => {
    try {
      await request.post('/admin/llm/active-provider', { provider });
      setActiveProvider(provider);
      message.success(`已切换激活 Provider: ${provider}`);
      loadProviders();
    } catch (err) {
      message.error((err as Error)?.message || '切换失败');
    }
  };

  const handleSearch = async () => {
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await request.get<{ items: LLMModelSearchResult[]; keyword: string | null }>(
        '/admin/llm/models/search',
        { params: { provider: tabProvider, keyword: keyword || undefined } },
      );
      setSearchResults(res.data.items);
      if (res.data.items.length === 0) {
        message.info(`未找到${keyword ? ` "${keyword}" ` : ''}匹配的模型`);
      }
    } catch (err) {
      message.error((err as Error)?.message || '搜索失败');
    } finally {
      setSearching(false);
    }
  };

  const handleSetDefault = async (row: LLMModelSearchResult) => {
    if (row.provider === 'closeai') {
      setPriceModalTarget(row);
      priceForm.setFieldsValue({
        displayName: row.displayName || undefined,
        inputPricePerMtok: row.inputPricePerMtok ? Number(row.inputPricePerMtok) : 0,
        outputPricePerMtok: row.outputPricePerMtok ? Number(row.outputPricePerMtok) : 0,
      });
      setPriceModalOpen(true);
      return;
    }
    // OpenRouter: use upstream prices directly
    try {
      await request.post('/admin/llm/active-model', {
        provider: row.provider,
        modelId: row.modelId,
        displayName: row.displayName,
        inputPricePerMtok: row.inputPricePerMtok,
        outputPricePerMtok: row.outputPricePerMtok,
      });
      message.success(`已设为 ${row.provider} 的默认：${row.modelId}`);
      loadActive(row.provider);
    } catch (err) {
      message.error((err as Error)?.message || '设置失败');
    }
  };

  const confirmPriceModal = async () => {
    try {
      const values = await priceForm.validateFields();
      if (!priceModalTarget) return;
      await request.post('/admin/llm/active-model', {
        provider: priceModalTarget.provider,
        modelId: priceModalTarget.modelId,
        displayName: values.displayName || null,
        inputPricePerMtok: values.inputPricePerMtok,
        outputPricePerMtok: values.outputPricePerMtok,
      });
      message.success(`已设为 ${priceModalTarget.provider} 的默认：${priceModalTarget.modelId}`);
      setPriceModalOpen(false);
      setPriceModalTarget(null);
      priceForm.resetFields();
      loadActive(priceModalTarget.provider);
    } catch (err) {
      if ((err as { errorFields?: unknown }).errorFields) return; // form validation
      message.error((err as Error)?.message || '保存失败');
    }
  };

  const openEditPricesModal = (provider: string) => {
    const active = activeModels[provider];
    if (!active) return;
    setPriceModalTarget({
      provider: active.provider,
      modelId: active.modelId,
      displayName: active.displayName,
      contextLength: active.contextLength,
      inputPricePerMtok: active.inputPricePerMtok,
      outputPricePerMtok: active.outputPricePerMtok,
      priceSource: active.priceSource,
    });
    priceForm.setFieldsValue({
      displayName: active.displayName || undefined,
      inputPricePerMtok: active.inputPricePerMtok ? Number(active.inputPricePerMtok) : 0,
      outputPricePerMtok: active.outputPricePerMtok ? Number(active.outputPricePerMtok) : 0,
    });
    setPriceModalOpen(true);
  };

  const persistResult = (provider: string, entry: TestResultEntry) => {
    setTestResults((prev) => {
      const next = { ...prev, [provider]: entry };
      saveTestResults(next);
      return next;
    });
  };

  const handleTest = async (provider: string) => {
    setTestingProvider(provider);
    const testedAt = new Date().toISOString();
    try {
      const res = await request.post<LLMTestResponse>('/admin/test-llm', { provider });
      const r = res.data;
      persistResult(provider, {
        success: r.success,
        model: r.model,
        latencyMs: r.latencyMs,
        error: r.success ? undefined : r.error || '未知错误',
        testedAt,
      });
      if (r.success) {
        message.success(`测试成功 · ${r.model} · ${r.latencyMs}ms`);
      } else {
        message.error(`测试失败：${r.error || '未知错误'}（${r.latencyMs}ms）`);
      }
    } catch (err) {
      const errMsg = (err as Error)?.message || '测试失败';
      persistResult(provider, { success: false, error: errMsg, testedAt });
      message.error(errMsg);
    } finally {
      setTestingProvider(null);
    }
  };

  const saveMarkup = async () => {
    try {
      await request.patch('/admin/llm/settings', { closeaiMarkupRatio: markupInput });
      message.success('已更新 CloseAI 倍率');
      loadSettings();
    } catch (err) {
      message.error((err as Error)?.message || '更新失败');
    }
  };

  const searchColumns: ColumnsType<LLMModelSearchResult> = [
    {
      title: 'Model',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.modelId}</Text>
          {r.displayName && r.displayName !== r.modelId && (
            <Text type="secondary" style={{ fontSize: 12 }}>{r.displayName}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '输入价 ($/M)',
      width: 140,
      render: (_, r) => formatPrice(r.inputPricePerMtok),
    },
    {
      title: '输出价 ($/M)',
      width: 140,
      render: (_, r) => formatPrice(r.outputPricePerMtok),
    },
    {
      title: '来源',
      width: 100,
      render: (_, r) => (
        <Tag color={r.priceSource === 'upstream' ? 'green' : 'blue'}>
          {r.priceSource === 'upstream' ? '厂商' : '建议/手动'}
        </Tag>
      ),
    },
    {
      title: '上下文',
      width: 110,
      render: (_, r) => (r.contextLength ? r.contextLength.toLocaleString() : '—'),
    },
    {
      title: '操作',
      width: 170,
      fixed: 'right',
      render: (_, r) => {
        const isActive =
          activeModels[r.provider]?.modelId === r.modelId;
        return isActive ? (
          <Tag icon={<CheckCircleFilled />} color="success">
            当前默认
          </Tag>
        ) : (
          <Button size="small" type="primary" onClick={() => handleSetDefault(r)}>
            设为此 provider 的默认
          </Button>
        );
      },
    },
  ];

  const renderActiveCard = (provider: string) => {
    const active = activeModels[provider];
    if (!active) {
      return (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Empty description={`${provider} 尚未设置默认模型，请在下方搜索并设为默认`} />
        </Card>
      );
    }
    return (
      <Card
        size="small"
        title="当前默认模型"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Button
              size="small"
              icon={<ExperimentOutlined />}
              loading={testingProvider === provider}
              onClick={() => handleTest(provider)}
            >
              测试
            </Button>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditPricesModal(provider)}
            >
              编辑
            </Button>
          </Space>
        }
      >
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Model ID">
            <Text code>{active.modelId}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Display Name">{active.displayName || '—'}</Descriptions.Item>
          <Descriptions.Item label="输入价">
            {formatPrice(active.inputPricePerMtok)} / 1M tokens
          </Descriptions.Item>
          <Descriptions.Item label="输出价">
            {formatPrice(active.outputPricePerMtok)} / 1M tokens
          </Descriptions.Item>
          <Descriptions.Item label="价格来源">
            <Tag color={active.priceSource === 'upstream' ? 'green' : 'blue'}>
              {active.priceSource === 'upstream' ? '厂商权威' : '手动'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="上下文">
            {active.contextLength ? active.contextLength.toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间" span={2}>
            {dayjs(active.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label="最近测试" span={2}>
            {(() => {
              const r = testResults[provider];
              if (!r) return <Text type="secondary">未测试</Text>;
              return (
                <Space wrap size={8}>
                  {r.success ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">成功</Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />} color="error">失败</Tag>
                  )}
                  {r.model && <Text code>{r.model}</Text>}
                  {r.latencyMs != null && <Text type="secondary">{r.latencyMs}ms</Text>}
                  <Text type="secondary">
                    @ {dayjs(r.testedAt).format('MM-DD HH:mm:ss')}
                  </Text>
                  {r.error && (
                    <Tooltip title={r.error}>
                      <Text type="danger" style={{ maxWidth: 360 }} ellipsis>
                        {r.error}
                      </Text>
                    </Tooltip>
                  )}
                </Space>
              );
            })()}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  };

  const activeSummary = activeModels[activeProvider];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>
        LLM 模型管理
      </Title>

      {/* Active provider top bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={24}>
          <Col>
            <Text strong>系统激活 Provider：</Text>
          </Col>
          <Col>
            <Segmented
              value={activeProvider}
              onChange={(v) => handleSwitchActiveProvider(v as string)}
              options={PROVIDERS.map((p) => {
                const cfg = providersInfo?.providers.find((x) => x.id === p.key);
                return {
                  label: (
                    <span>
                      {p.label}
                      {cfg && !cfg.configured && (
                        <Tag color="red" style={{ marginLeft: 6 }}>未配置</Tag>
                      )}
                    </span>
                  ),
                  value: p.key,
                };
              })}
            />
          </Col>
          <Col flex="auto">
            {activeSummary ? (
              <Text type="secondary">
                当前默认模型：<Text code>{activeSummary.modelId}</Text> · 输入{' '}
                {formatPrice(activeSummary.inputPricePerMtok)}/M · 输出{' '}
                {formatPrice(activeSummary.outputPricePerMtok)}/M
              </Text>
            ) : (
              <Text type="warning">当前 Provider 未设默认模型，将回退到 env 默认</Text>
            )}
          </Col>
        </Row>
      </Card>

      {/* Global settings */}
      <Card size="small" style={{ marginBottom: 16 }} title="全局设置">
        <Row gutter={16} align="middle">
          <Col>
            <Text>CloseAI 计费倍率：</Text>
          </Col>
          <Col>
            <InputNumber
              min={0.1}
              max={10}
              step={0.05}
              value={markupInput}
              onChange={(v) => setMarkupInput(v || 1.5)}
              style={{ width: 120 }}
            />
          </Col>
          <Col>
            <Button type="primary" onClick={saveMarkup}>
              保存
            </Button>
          </Col>
          <Col flex="auto">
            <Text type="secondary" style={{ marginLeft: 16 }}>
              CloseAI 是 OpenAI 代理，实际费用 = 官方价 × 倍率。倍率建议按套餐设为 1.1 / 1.25 / 1.5。
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Per-provider tabs */}
      <Tabs
        activeKey={tabProvider}
        onChange={(k) => {
          setTabProvider(k);
          setSearchResults([]);
          setKeyword('');
        }}
        items={PROVIDERS.map((p) => ({
          key: p.key,
          label: p.label,
          children: (
            <div>
              {renderActiveCard(p.key)}

              <Card size="small" title="搜索并设为默认">
                <Space.Compact style={{ width: '100%', maxWidth: 600, marginBottom: 16 }}>
                  <Input
                    placeholder="关键词（可选），如 gpt-4o / claude"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onPressEnter={handleSearch}
                  />
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={searching}
                  >
                    搜索
                  </Button>
                </Space.Compact>

                <Table<LLMModelSearchResult>
                  rowKey={(r) => `${r.provider}:${r.modelId}`}
                  size="small"
                  dataSource={searchResults}
                  columns={searchColumns}
                  scroll={{ x: 900 }}
                  loading={searching}
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  locale={{
                    emptyText: <Empty description="输入关键词并点搜索" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                  }}
                />
              </Card>
            </div>
          ),
        }))}
      />

      {/* Price modal for CloseAI / edit */}
      <Modal
        title={
          priceModalTarget
            ? `设置价格：${priceModalTarget.modelId}（${priceModalTarget.provider}）`
            : '设置价格'
        }
        open={priceModalOpen}
        onCancel={() => {
          setPriceModalOpen(false);
          setPriceModalTarget(null);
          priceForm.resetFields();
        }}
        onOk={confirmPriceModal}
        okText="保存并设为默认"
        width={520}
      >
        <Form form={priceForm} layout="vertical">
          <Form.Item name="displayName" label="显示名称（可选）">
            <Input placeholder="可留空" />
          </Form.Item>
          <Form.Item
            name="inputPricePerMtok"
            label="输入价（USD / 1M tokens）"
            rules={[{ required: true, message: '必填', type: 'number', min: 0 }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="例如 0.15" />
          </Form.Item>
          <Form.Item
            name="outputPricePerMtok"
            label="输出价（USD / 1M tokens）"
            rules={[{ required: true, message: '必填', type: 'number', min: 0 }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="例如 0.60" />
          </Form.Item>
          {priceModalTarget?.provider === 'closeai' && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              CloseAI 是 OpenAI 代理，建议填入 OpenAI 官方公开价（建议值已尝试从 OpenRouter
              的同名模型交叉查询自动预填）。实际计费时还会乘 CloseAI 倍率（上方设置）。
            </Text>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default LLMModelsPage;
